import { useEffect, useState } from 'react';
import { Button, Modal, Form, Toast, ToastContainer } from 'react-bootstrap';
import { Pencil, Plus, Trash } from 'react-bootstrap-icons'; 
import DataTable from 'react-data-table-component';
import api from '../api';

const Empresas = () => {
  const [empresas, setEmpresas] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [errors, setErrors] = useState({});
  const [filteredEmpresas, setFilteredEmpresas] = useState([]);
  const [search, setSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [empresaAEliminar, setEmpresaAEliminar] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  const normalizeText = (text) => {
    return text
      ?.toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, ""); // quita acentos
  };

  useEffect(() => {
    api.get('/empresas').then(res => {
      setEmpresas(res.data)
      setFilteredEmpresas(res.data);
    });
  }, []);

  useEffect(() => {
    const result = empresas.filter(item =>
      normalizeText(item.nombre)?.includes(normalizeText(search)) ||
      normalizeText(item.nit)?.includes(normalizeText(search)) ||
      normalizeText(item.telefono)?.includes(normalizeText(search)) ||
      normalizeText(item.correo)?.includes(normalizeText(search)) ||
      normalizeText(item.nombre_contacto)?.includes(normalizeText(search))
    );
    setFilteredEmpresas(result);
  }, [search, empresas]);

  const mostrarToast = (variant, message) => {
    setToast({ show: true, variant, message });

    setTimeout(() => {
      setToast({ ...toast, show: false });
    }, 3000);
  };

  const validate = () => {
    const newErrors = {};

    if (!selectedEmpresa.nombre?.trim()) newErrors.nombre = "Campo requerido";
    if (!selectedEmpresa.nit?.trim()) {
      newErrors.nit = "Campo requerido";
    } else {
      const nitExistente = empresas.find(
        (e) => e.nit === selectedEmpresa.nit && e.id !== selectedEmpresa.id
      );

      if (nitExistente) {
        newErrors.nit = "El NIT ya está registrado";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleShowModal = (empresa = {}) => {
    setSelectedEmpresa({
      id: empresa.id || null,
      nombre: empresa.nombre || '',
      nit: empresa.nit || '',
      telefono: empresa.telefono || '',
      correo: empresa.correo || '',
      nombre_contacto: empresa.nombre_contacto || '',
    });
    setErrors({});
    setShowModal(true);
  };


  const handleInputChange = (e) => {
    setSelectedEmpresa({
      ...selectedEmpresa,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = () => {
    if (!validate()) return;

    setIsSaving(true);
    const isEdit = !!selectedEmpresa.id;
    const request = isEdit
      ? api.put(`/empresas/${selectedEmpresa.id}`, selectedEmpresa)
      : api.post('/empresas', selectedEmpresa);

    request
      .then(res => {
        if (isEdit) {
          const updatedList = empresas.map(emp => emp.id === res.data.data.id ? res.data.data : emp)
          setEmpresas(updatedList);
          setFilteredEmpresas(updatedList)
          mostrarToast('success', 'Empresa actualizada correctamente');
        } else {
          const nuevo = res.data.data;
          const nuevaLista = [...empresas, nuevo];
          setEmpresas(nuevaLista);
          setFilteredEmpresas(nuevaLista);
          mostrarToast('success', 'Empresa creada correctamente');
        }
        setShowModal(false);
      })
      .catch(err => {
        if (err.response?.data?.errors) {
          setErrors(err.response.data.errors);
          mostrarToast('danger', 'No se pudo realizar la acción');
        }
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleDelete = (row) => {
    setEmpresaAEliminar(row);
    setShowDeleteModal(true);
  };

  const confirmarEliminacion = () => {
    setIsDeleting(true);
    api.delete(`/empresas/${empresaAEliminar.id}`)
      .then(() => {
        const listaNueva = empresas.filter(emp => emp.id !== empresaAEliminar.id);
        setEmpresas(listaNueva);
        mostrarToast('success', 'Empresa eliminada correctamente.');
      })
      .catch(() => mostrarToast('danger', 'Error al eliminar la empresa.'))
      .finally(() => {
        setIsDeleting(false);
      });
    setShowDeleteModal(false);
  };

  const columns = [
    { name: 'Empresa', selector: row => row.nombre, sortable: true },
    { name: 'Nit', selector: row => row.nit, sortable: true },
    { name: 'Teléfono', selector: row => row.telefono, sortable: true },
    { name: 'Correo', selector: row => row.correo, sortable: true },
    { name: 'Contacto', selector: row => row.nombre_contacto, sortable: true },
    {
      name: 'Opciones',
      cell: row => (
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleShowModal(row)}
          >
            <Pencil />
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleDelete(row)}
          >
            <Trash />
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];
  

  return (
    <div className="mt-4">
      <DataTable
        title="Listado de Empresas"
        columns={columns}
        data={filteredEmpresas}
        pagination
        highlightOnHover
        responsive
        striped
        subHeader
        subHeaderComponent={
          <div className="d-flex justify-content-between w-100">
            <Button variant="outline-success" size="sm" onClick={handleShowModal}>
              Nuevo <Plus />
            </Button>
            <input
              type="text"
              className="form-control w-25"
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        }
      />

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedEmpresa?.id ? 'Editar Empresa' : 'Nueva Empresa'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEmpresa && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  type="text"
                  name="nombre"
                  value={selectedEmpresa.nombre}
                  onChange={handleInputChange}
                  isInvalid={!!errors.nombre}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.nombre}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Nit</Form.Label>
                <Form.Control
                  type="text"
                  name="nit"
                  value={selectedEmpresa.nit}
                  onChange={handleInputChange}
                  isInvalid={!!errors.nit}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.nit}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Teléfono</Form.Label>
                <Form.Control
                  type="text"
                  name="telefono"
                  value={selectedEmpresa.telefono}
                  onChange={handleInputChange}
                  isInvalid={!!errors.telefono}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.telefono}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Correo electrónico</Form.Label>
                <Form.Control
                  type="text"
                  name="correo"
                  value={selectedEmpresa.correo}
                  onChange={handleInputChange}
                  isInvalid={!!errors.correo}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.correo}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Nombre de contacto</Form.Label>
                <Form.Control
                  type="text"
                  name="nombre_contacto"
                  value={selectedEmpresa.nombre_contacto}
                  onChange={handleInputChange}
                  isInvalid={!!errors.nombre_contacto}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.nombre_contacto}
                </Form.Control.Feedback>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Guardando...' : 'Guardar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmación para eliminar */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Está seguro de que desea eliminar la empresa <strong>{empresaAEliminar?.nombre}</strong>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger"
            onClick={confirmarEliminacion}
            disabled={isDeleting}
          >
            {isDeleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Mensaje Toast */}
      <ToastContainer position="top-end" className="p-3">
        <Toast
          bg={toast.variant}
          onClose={() => setToast({ ...toast, show: false })}
          show={toast.show}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default Empresas;
